import { useState } from 'react';
import { signInWith, type Provider } from '../../lib/auth';
import { SsoButton } from './SsoButton';
import { TermsLine } from './TermsLine';
import { COMPANY } from '../../lib/company';

type Props = {
  accent: string;
};

export function LoginForm({ accent }: Props) {
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const go = async (provider: Provider) => {
    if (!agreed || busy) return;
    setBusy(provider);
    setError(null);
    try {
      await signInWith(provider);
      // signInWithPopup resolves once the popup closes. The auth state listener
      // in useNexusAuthorization will pick it up from here.
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Sign-in failed';
      setError(message);
      setBusy(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, position: 'relative' }}>
      <div>
        <p className="eyebrow" style={{ color: accent, marginBottom: 10 }}>
          Secure sign in
        </p>
        <h1 className="h2" style={{ fontSize: 36, lineHeight: 1.05 }}>
          Welcome to <br />
          jones+palmer tools.
        </h1>
        <p className="body muted" style={{ marginTop: 14, maxWidth: 380 }}>
          A single workspace for our internal and client-facing tools. Sign in with your work
          account to continue.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6 }}>
        <SsoButton
          provider="microsoft"
          disabled={!agreed || busy !== null}
          onClick={() => void go('microsoft')}
          accent={accent}
        />
        <SsoButton
          provider="google"
          disabled={!agreed || busy !== null}
          onClick={() => void go('google')}
          accent={accent}
        />
      </div>

      <div style={{ height: 1, background: 'rgba(14,14,14,.10)', margin: '4px 0' }} />

      <TermsLine checked={agreed} onToggle={setAgreed} accent={accent} />

      {error && (
        <div
          role="alert"
          style={{
            fontSize: 13,
            color: '#a23',
            background: 'rgba(255,80,80,.08)',
            border: '1px solid rgba(255,80,80,.24)',
            padding: '8px 12px',
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      <p style={{ margin: '2px 0 0', fontSize: 13, color: 'rgba(14,14,14,.62)' }}>
        Access is granted by Jones + Palmer. Need an account, or having trouble signing in?{' '}
        <a
          href={`mailto:${COMPANY.email}?subject=${encodeURIComponent('jp.tools access request')}`}
          style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          Email us
        </a>{' '}
        or call{' '}
        <a
          href={COMPANY.phoneHref}
          style={{ color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          {COMPANY.phone}
        </a>
        .
      </p>

      {busy && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(245,236,217,.7)',
            backdropFilter: 'blur(4px)',
            display: 'grid',
            placeItems: 'center',
            borderRadius: 'inherit',
            zIndex: 5,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                border: `2.5px solid ${accent}33`,
                borderTopColor: accent,
                animation: 'spin 1s linear infinite',
              }}
            />
            <div className="mono" style={{ color: 'rgba(14,14,14,.6)' }}>
              Authenticating with {busy}…
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
